import { buildVenueProductSavePayload } from 'src/utils/venueProductSavePayload'
import { isVenueProductExcludedFromGrossProfit } from 'src/utils/excludeFromGrossProfit'
import {
  getParLevelForProduct,
  normalizeParLevelInput,
  PAR_LEVEL_LIMIT_ERROR_CODE,
  previewParLevelSave,
} from 'src/utils/parLevelPaywall'

export const VENUE_PRODUCT_INLINE_EDIT_FIELDS = [
  'title',
  'defaultQuantity',
  'parLevel',
  'excludeFromGrossProfit',
  'nominalCode',
]

const toDefaultQuantity = value => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }

  return parsed
}

export const formatVenueProductInlineDisplayValue = (field, record) => {
  switch (field) {
    case 'title':
      return record?.title ?? ''
    case 'defaultQuantity':
      return String(record?.defaultQuantity ?? 1)
    case 'parLevel':
      return String(getParLevelForProduct(record))
    case 'excludeFromGrossProfit':
      return isVenueProductExcludedFromGrossProfit(record) ? 'Yes' : 'No'
    case 'nominalCode':
      return record?.nominalCode || '—'
    default:
      return ''
  }
}

export const formatVenueProductInlineEditValue = (field, record) => {
  switch (field) {
    case 'title':
      return record?.title ?? ''
    case 'defaultQuantity':
      return String(record?.defaultQuantity ?? 1)
    case 'parLevel':
      return String(getParLevelForProduct(record))
    case 'excludeFromGrossProfit':
      return isVenueProductExcludedFromGrossProfit(record)
    case 'nominalCode':
      return record?.nominalCode ?? ''
    default:
      return ''
  }
}

export const normalizeVenueProductInlineValue = (field, rawValue) => {
  switch (field) {
    case 'title': {
      const value = String(rawValue ?? '').trim()
      if (!value) {
        return { value: null, error: 'Title cannot be empty.' }
      }

      return { value, error: null }
    }
    case 'defaultQuantity': {
      const value = toDefaultQuantity(rawValue)
      if (value === null) {
        return {
          value: null,
          error: 'Please enter a valid quantity greater than zero.',
        }
      }

      return { value, error: null }
    }
    case 'parLevel': {
      const value = Number(normalizeParLevelInput(rawValue))
      if (!Number.isFinite(value) || value < 0 || value > 999) {
        return {
          value: null,
          error: 'Par level must be a number between 0 and 999.',
        }
      }

      return { value, error: null }
    }
    case 'excludeFromGrossProfit':
      return {
        value: rawValue === true || rawValue === 'true',
        error: null,
      }
    case 'nominalCode':
      return { value: String(rawValue ?? '').trim(), error: null }
    default:
      return { value: null, error: 'This field cannot be edited inline.' }
  }
}

export const venueProductInlineFieldUnchanged = (field, record, nextValue) => {
  switch (field) {
    case 'title':
      return String(record?.title ?? '').trim() === String(nextValue).trim()
    case 'defaultQuantity':
      return (Number(record?.defaultQuantity) || 1) === Number(nextValue)
    case 'parLevel':
      return (
        getParLevelForProduct(record) ===
        getParLevelForProduct({ parLevel: nextValue })
      )
    case 'excludeFromGrossProfit':
      return (
        isVenueProductExcludedFromGrossProfit(record) ===
        isVenueProductExcludedFromGrossProfit({
          excludeFromGrossProfit: nextValue,
        })
      )
    case 'nominalCode':
      return (
        String(record?.nominalCode ?? '').trim() === String(nextValue).trim()
      )
    default:
      return true
  }
}

export const buildVenueProductInlineSavePayload = ({
  products,
  productId,
  field,
  value,
  venueId,
}) => {
  const product = products.find(item => item._id === productId)
  if (!product) {
    return null
  }

  return buildVenueProductSavePayload({ ...product, [field]: value }, venueId)
}

export const persistVenueProductInlineEdit = async ({
  dispatch,
  editVenueProduct,
  products,
  venueId,
  productId,
  field,
  rawValue,
  plan = 'Free',
  onParLevelSaved,
}) => {
  const product = products.find(item => item._id === productId)
  if (!product) {
    return { ok: false, error: 'Product not found.' }
  }

  const { value, error } = normalizeVenueProductInlineValue(field, rawValue)
  if (error) {
    return { ok: false, error }
  }

  if (venueProductInlineFieldUnchanged(field, product, value)) {
    return { ok: true, skipped: true }
  }

  if (field === 'parLevel') {
    const preview = previewParLevelSave({
      venueProducts: products,
      venueProductId: productId,
      newParLevel: value,
      plan,
    })

    if (preview.blocked) {
      return { ok: false, paywallBlocked: true, paywallSource: 'client' }
    }
  }

  const payload = buildVenueProductInlineSavePayload({
    products,
    productId,
    field,
    value,
    venueId,
  })

  if (!payload) {
    return { ok: false, error: 'Product not found.' }
  }

  const previousParLevel =
    field === 'parLevel' ? getParLevelForProduct(product) : null

  const result = await dispatch(editVenueProduct(payload))

  if (result === PAR_LEVEL_LIMIT_ERROR_CODE) {
    return { ok: false, paywallBlocked: true, paywallSource: 'server' }
  }

  if (!result) {
    return { ok: false, error: 'Unable to save right now. Please try again.' }
  }

  if (field === 'parLevel' && onParLevelSaved) {
    onParLevelSaved({
      venueProductId: productId,
      previousParLevel,
      newParLevel: value,
    })
  }

  return { ok: true }
}

export const persistVenueProductExcludeFromGrossProfitToggle = ({
  dispatch,
  editVenueProduct,
  products,
  venueId,
  productId,
}) => {
  const product = products.find(item => item._id === productId)
  if (!product) {
    return Promise.resolve({ ok: false, error: 'Product not found.' })
  }

  return persistVenueProductInlineEdit({
    dispatch,
    editVenueProduct,
    products,
    venueId,
    productId,
    field: 'excludeFromGrossProfit',
    rawValue: !isVenueProductExcludedFromGrossProfit(product),
  })
}
